import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usePensionStore } from '../store/pensionStore.ts';

interface AddYearModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddYearModal: React.FC<AddYearModalProps> = ({ isOpen, onClose }) => {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const { addYear, years } = usePensionStore();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Generate year options (current year - 15 to current year + 10)
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(() => {
    const options = [];
    for (let year = currentYear - 15; year <= currentYear + 10; year++) {
      options.push(year);
    }
    return options;
  }, [currentYear]);
  const existingYearIds = useMemo(() => new Set(years.map(year => year.id)), [years]);

  useEffect(() => {
    if (isOpen) {
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
      
      // Set focus to modal when opened for accessibility
      const modal = document.getElementById('add-year-modal');
      modal?.focus();
      
      // Find the first available year to select (prefer current year)
      
      let availableYear = null;
      if (!existingYearIds.has(currentYear)) {
        availableYear = currentYear;
      } else {
        // Look for first available year from current year backwards, then forwards
        for (let year = currentYear - 1; year >= currentYear - 15; year--) {
          if (!existingYearIds.has(year)) {
            availableYear = year;
            break;
          }
        }
        
        if (!availableYear) {
          for (let year = currentYear + 1; year <= currentYear + 10; year++) {
            if (!existingYearIds.has(year)) {
              availableYear = year;
              break;
            }
          }
        }
      }
      
      setSelectedYear(availableYear);
    } else {
      // Restore background scrolling when modal closes
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup function to restore scrolling if component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, years, currentYear, existingYearIds]);

  // Scroll to selected year when it changes
  useEffect(() => {
    if (selectedYear !== null && scrollContainerRef.current && isOpen) {
      const yearIndex = yearOptions.findIndex(year => year === selectedYear);
      if (yearIndex !== -1) {
        const itemHeight = 48; // Height of each year item
        const containerHeight = 240; // Height of visible area
        const paddingTop = 96; // py-24 = 96px top padding
        const scrollTop = Math.max(0, paddingTop + (yearIndex * itemHeight) - (containerHeight / 2) + (itemHeight / 2));
        setTimeout(() => {
          scrollContainerRef.current?.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        }, 100); // Small delay to ensure modal is rendered
      }
    }
  }, [selectedYear, yearOptions, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedYear === null) {
      alert('Please select a year to add.');
      return;
    }
    
    // Check if year already exists
    const existingYear = years.find(year => year.id === selectedYear);
    if (existingYear) {
      alert('This year already exists in your calculation.');
      return;
    }

    addYear(selectedYear);
    onClose();
  };

  const handleClose = () => {
    document.body.style.overflow = 'unset';
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      id="add-year-modal"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Add New Year</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Tax Year
              </label>
              
              {/* Year Dialer */}
              <div className="relative">
                <div 
                  ref={scrollContainerRef}
                  className="h-60 overflow-y-auto border border-gray-300 rounded-lg bg-white scrollbar-thin scrollbar-thumb-gray-300"
                  style={{ scrollbarWidth: 'thin' }}
                >
                  <div className="py-24"> {/* Padding to center the selection */}
                    {yearOptions.map((year) => {
                      const isExisting = existingYearIds.has(year);
                      const isSelected = selectedYear === year;
                      
                      return (
                        <button
                          key={year}
                          type="button"
                          onClick={() => !isExisting && setSelectedYear(year)}
                          disabled={isExisting}
                          className={`
                            w-full px-4 py-3 text-left transition-all duration-150 border-none bg-transparent h-12 flex items-center justify-between
                            ${
                              isSelected 
                                ? 'bg-blue-100 text-blue-800 font-semibold border-2 border-blue-500 shadow-md' 
                                : isExisting 
                                  ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                                  : 'text-gray-900 hover:bg-gray-50 cursor-pointer'
                            }
                          `}
                        >
                          <span>
                            Tax Year {year}/{(year + 1).toString().slice(-2)}
                          </span>
                          {isExisting && (
                            <span className="text-xs text-gray-400 font-normal">
                              Already added
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
              </div>
              
              <p className="text-sm text-gray-500 mt-2">
                Tax year runs from 6 April to 5 April the following year
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-3 min-h-[44px] border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={selectedYear === null || existingYearIds.has(selectedYear)}
                className="flex-1 px-4 py-3 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Add Year
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};